/** Sidebar selection is the server's estimate, not the draft revision: typing does not reread account/history. */
import { useQuery } from '@tanstack/react-query'
import { SHOPIFY_STUDY_BUILDER_SIDEBAR_OPERATION, type StudyBuilderSidebar } from '@testfeed/shopify-contracts'
import { shopifyQueryKey, useShopifyAppContext } from '../../ShopifyAppContext'
import { shopifyApiFetch, type ShopifyAppBridge } from '../../shopifyApiFetch'
import { throwMerchantFacingApiError } from '../../shopifyApiError'
import { retryNetworkOnly } from '../../page-state'
import { shopifyQueryPolicies } from '../../queries/shopifyQueryPolicies'
import { parseContractPayload } from '../../merchantErrors'

export type StudyBuilderSidebarRead = {
  data: StudyBuilderSidebar | undefined
  isError: boolean
  isFetching: boolean
  refetch: () => Promise<unknown>
}

export function useStudyBuilderSidebarQuery(shopify: ShopifyAppBridge, creditEstimate: number | null | undefined) {
  const { shopDomain, orgId, connectionId } = useShopifyAppContext()
  return useQuery({
    queryKey: shopifyQueryKey(shopDomain, orgId, connectionId, ['study-builder-sidebar', creditEstimate] as const),
    enabled: creditEstimate !== undefined && Boolean(shopDomain && orgId && connectionId),
    ...shopifyQueryPolicies.usage,
    retry: retryNetworkOnly,
    queryFn: async ({ signal }) => {
      const response = await shopifyApiFetch(shopify, SHOPIFY_STUDY_BUILDER_SIDEBAR_OPERATION.path, {
        init: {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ creditEstimate }),
          signal,
        },
      })
      if (!response.ok) await throwMerchantFacingApiError(response, SHOPIFY_STUDY_BUILDER_SIDEBAR_OPERATION.id)
      const sidebar = parseContractPayload(
        'studyBuilderSidebar',
        SHOPIFY_STUDY_BUILDER_SIDEBAR_OPERATION.responseSchema,
        await response.json(),
      )
      if (sidebar.creditEstimate !== creditEstimate) throw new Error('STUDY_BUILDER_SIDEBAR_ESTIMATE_MISMATCH')
      return sidebar
    },
  })
}
